import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '@supabase/supabase-js';
import { UserProfile } from './types';

// --- Auth Store ---
interface AuthState {
  user: User | null;
  userProfile: UserProfile | null;
  isLoading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isManager: boolean;
  accessibleComponents: string[];

  // Actions
  setUser: (user: User | null) => void;
  setUserProfile: (profile: UserProfile | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setAccessibleComponents: (components: string[]) => void;
  hasAccess: (componentPath: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      userProfile: null,
      isLoading: true,
      isAdmin: false,
      isSuperAdmin: false,
      isManager: false,
      accessibleComponents: [],

      /**
       * Define o usuário autenticado.
       * Se o usuário for null (logout), limpa também o perfil, isAdmin e accessibleComponents.
       */
      setUser: (user) => {
        if (user === null) {
          set({
            user: null,
            userProfile: null,
            isAdmin: false,
            isSuperAdmin: false,
            isManager: false,
            accessibleComponents: [],
          });
        } else {
          set({ user });
        }
      },

      /**
       * Define o perfil do usuário e atualiza o status de admin.
       */
      setUserProfile: (profile) => {
        const roleName = profile?.role?.role_name || '';
        
        set({
          userProfile: profile,
          isAdmin: roleName === 'Admin',
          isSuperAdmin: roleName === 'Super Admin',
          isManager: roleName === 'Manager',
        });
      },

      /**
       * Define o estado de carregamento.
       */
      setIsLoading: (isLoading) => set({ isLoading }),

      /**
       * Define a lista de componentes/paths acessíveis para o usuário.
       */
      setAccessibleComponents: (components) => {
        // console.log('[AuthStore] Setting accessible components:', components);
        set({ accessibleComponents: components });
      },

      /**
       * Verifica se o usuário tem acesso a um determinado componente/path.
       * Super Admin tem acesso a tudo.
       * Admin tem acesso com base na lista de componentes acessíveis.
       */
      hasAccess: (componentPath) => {
        const { accessibleComponents, isAdmin, isSuperAdmin, isManager } = get();
        
        // Super Admin tem acesso a tudo
        if (isSuperAdmin) {
          return true;
        }
        
        // Admin ou Manager também pode ter acesso a componentes específicos
        if (isAdmin || isManager) {
          const hasAccess = accessibleComponents.includes(componentPath);
          // // console.log(`[hasAccess] Checking ${componentPath}: ${hasAccess ? 'GRANTED' : 'DENIED'}`);
          // console.log(`[hasAccess] Available components: ${accessibleComponents.join(', ') || 'NONE'}`);
          return hasAccess;
        }
        
        // Usuários padrão não têm acesso a componentes administrativos
        return false;
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({ user: state.user }),
    }
  )
);

/**
 * Documentação do useAuthStore:
 *
 * Estado (State):
 * - `user`: Objeto User do Supabase ou null se não autenticado.
 * - `userProfile`: Perfil do usuário (com roles/permissões) ou null.
 * - `isLoading`: Booleano indicando se os dados de autenticação/perfil estão sendo carregados.
 * - `isAdmin`: Booleano indicando se o usuário é um administrador.
 * - `isSuperAdmin`: Booleano indicando se o usuário é um super administrador.
 * - `isManager`: Booleano indicando se o usuário é um gerente.
 * - `accessibleComponents`: Array de strings contendo os caminhos/identificadores dos componentes que o usuário pode acessar.
 *
 * Ações (Actions):
 * - `setUser(user)`: Define o objeto `user`. Se `null`, efetua logout limpando `userProfile`, `isAdmin` e `accessibleComponents`.
 * - `setUserProfile(profile)`: Define o `userProfile` e atualiza `isAdmin`, `isSuperAdmin` e `isManager`.
 * - `setIsLoading(loading)`: Controla o estado `isLoading`.
 * - `setAccessibleComponents(components)`: Define a lista de componentes acessíveis.
 * - `hasAccess(componentPath)`: Retorna `true` se o usuário tem acesso ao componente, `false` caso contrário.
 * Considera o tipo de role do usuário e a lista `accessibleComponents`.
 *
 * Persistência:
 * - O estado `user` é persistido no localStorage para manter o usuário logado entre sessões.
 */

// --- Theme Store ---
interface ThemeState {
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (isDark: boolean) => void;
}

export const useThemeStore = create<ThemeState>((set) => {
  // Tenta ler a preferência do localStorage
  const isDarkStored = localStorage.getItem('darkMode');
  // Define o estado inicial baseado no localStorage ou na preferência do sistema
  const initialIsDark = isDarkStored
    ? JSON.parse(isDarkStored)
    : typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  return {
    isDark: initialIsDark,
    /**
     * Alterna o tema entre claro e escuro e salva a preferência no localStorage.
     */
    toggleTheme: () =>
      set((state) => {
        const newIsDark = !state.isDark;
        localStorage.setItem('darkMode', JSON.stringify(newIsDark));
        return { isDark: newIsDark };
      }),
    /**
     * Define o tema (claro ou escuro) e salva a preferência no localStorage.
     */
    setTheme: (isDark) => {
      localStorage.setItem('darkMode', JSON.stringify(isDark));
      set({ isDark });
    },
  };
});

/**
 * Documentação do useThemeStore:
 *
 * Estado (State):
 * - `isDark`: Booleano indicando se o tema escuro está ativo.
 *
 * Ações (Actions):
 * - `toggleTheme()`: Alterna o tema atual (claro/escuro).
 * - `setTheme(isDark)`: Define o tema para claro ou escuro.
 *
 * Persistência:
 * - A preferência do tema (`darkMode`) é salva no `localStorage`.
 */

// --- Config Store ---
interface ConfigState {
  configs: Record<string, any>;
  setConfigs: (configs: Record<string, any>) => void;
  updateConfig: (key: string, value: any) => void;
}

export const useConfigStore = create<ConfigState>((set) => ({
  configs: {},
  /**
   * Substitui todas as configurações atuais pelas novas fornecidas.
   */
  setConfigs: (configs) => set({ configs }),
  /**
   * Adiciona ou atualiza uma configuração específica pelo sua chave.
   */
  updateConfig: (key, value) =>
    set((state) => ({
      configs: {
        ...state.configs,
        [key]: value,
      },
    })),
}));

/**
 * Documentação do useConfigStore:
 *
 * Estado (State):
 * - `configs`: Um objeto (dicionário) para armazenar pares chave-valor de configurações.
 *
 * Ações (Actions):
 * - `setConfigs(configs)`: Define o objeto de configurações completo.
 * - `updateConfig(key, value)`: Atualiza ou adiciona uma configuração específica.
 */

// --- UI Store ---
interface UiState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
}

export const useUiStore = create<UiState>((set) => ({
  sidebarCollapsed: false,
  /**
   * Alterna o estado de colapso do sidebar.
   */
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
}));

/**
 * Documentação do useUiStore:
 *
 * Estado (State):
 * - `sidebarCollapsed`: Booleano que indica se o menu lateral (sidebar) está recolhido.
 *
 * Ações (Actions):
 * - `toggleSidebar()`: Alterna o estado `sidebarCollapsed`.
 */